import { BreakPointCondition, useBreakpointIf, VStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { useEffect } from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { batchTicketCustomerBasketAdvisorDetailsQuery } from '../../../../store/batchTicketDetail/selector';
import { batchTicketOrderResumePdfTemplate } from '../../../BatchTicket/utils';
import { normalizeBatchTicketInfo } from '../../../BatchTicket/utils/PdfTemplate/normalizer';
import { BasketAdvisorListDataItem } from '../../../BatchTicket/utils/types';
import { getNotificationChannels } from '../../../OrderHistoryModal/components/OrderDetails/constants';
import { OrderHeaderDetails } from '../../../OrderHistoryModal/components/OrderHeaderDetails';
import { OrderPrincipalCardDetails } from '../../../OrderHistoryModal/components/OrderPrincipalCardDetails';
import { OrderStatusesSequence } from '../../../OrderHistoryModal/components/OrderStatusesSequence';
import { setupPDF } from '../../../Resume/utils';
import { BatchCustomerAccordion } from '../BatchCustomerAccordion';
import { OrderAccordionLoading } from '../OrderAccordionLoading';

export interface BatchDetailsProps {
  item: BasketAdvisorListDataItem;
  handleCloseDetails: () => void;
  handleShowErrorToast: (errorToShow: Error) => void;
}

export const BatchDetails = ({
  item,
  handleCloseDetails,
  handleShowErrorToast,
}: BatchDetailsProps) => {
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');

  const {
    data: basketData,
    error,
    loading,
  } = useRecoilQuery(
    batchTicketCustomerBasketAdvisorDetailsQuery({
      basketAdvisorId: item.basketAdvisorOrderId,
    }),
    { cachePolicy: 'no-cache' }
  );

  const basketCustomerData = basketData?.data;
  const orderName = item.basketOrderHash ? `#${item.basketOrderHash}` : item.basketAdvisorOrderId;

  const handleExportNewOrder = () => {
    (basketCustomerData || []).forEach((customerOrder) => {
      const orderHash = item.basketOrderHash || '';
      const customerId = customerOrder.basketCustomerOrder.customer.customerId || '';
      const pdfData = normalizeBatchTicketInfo(customerOrder.basketCustomerOrder, item);
      setupPDF(batchTicketOrderResumePdfTemplate(pdfData), `ordem-${orderHash}-${customerId}.pdf`);
    });
  };

  useEffect(() => {
    if (!error) {
      return;
    }
    handleShowErrorToast(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <VStack
      flex="1"
      padding={Spacing['inline.xxs']}
      space={Spacing['stack.xxxs']}
      bgColor={Color['neutral.white']}
      overflowY="scroll">
      <OrderHeaderDetails
        onClose={handleCloseDetails}
        isBasket
        lastUpdateDate={
          item.updateDate ? Utils.Mask.DateFormat(item.updateDate, 'dd/MM/yyyy HH:mm:ss') : '--'
        }
        onExportPress={basketCustomerData ? handleExportNewOrder : undefined}
        orderHasBasket={false}
        onBasketPress={() => null}
        orderName={orderName}
        isMobile={isMobile}
      />
      <OrderPrincipalCardDetails
        orderName={item.basketOrderHash || item.basketAdvisorOrderId}
        notificationChannels={getNotificationChannels(item)}
        orderDate={
          item.createDate ? Utils.Mask.DateFormat(item.createDate, 'dd/MM/yyyy HH:mm') : '--'
        }
        requestedAmount={Utils.Mask.Money(item.totalBasketDeliveredAmount)}
        orderHasBasket={false}
        onBasketPress={() => null}
        onExportPress={() => null}
        isMobile={isMobile}
        assetCount={item.totalBasketDeliveredOffers}
        clientCount={item.totalBasketDeliveredCustomers}
        requestUserFullName={item.requestUserFullName}
      />
      <OrderStatusesSequence isMobile={isMobile} />
      {!!loading && <OrderAccordionLoading quantity={item.totalCustomers} isMobile={isMobile} />}
      {!!basketCustomerData &&
        basketCustomerData.map((customerOrder) => (
          <BatchCustomerAccordion
            isLonely={item.totalCustomers === 1}
            key={customerOrder.basketCustomerOrder.basketCustomerOrderId}
            isMobile={isMobile}
            item={customerOrder.basketCustomerOrder}
            totalRequestedAmount={customerOrder.basketCustomerOrder.totalCustomerOffersAmount}
            handleShowErrorToast={handleShowErrorToast}
          />
        ))}
    </VStack>
  );
};
